import React from 'react'
import {
    StyledSettingBody,
    StyledPerthNowSettingOption,
    StyledSettingHeader,
    StyledSettingVerifyEmailBanner,
    StyledPerthNowWebLink,
    StyledFakeButton,
} from '../SettingScreen.styled'
import { SettingsScreenInnerProps } from '../SettingScreen'
import { ScreenType } from '../../hooks/useScreen'
import { IconPerthNowThumbnailPN, IconSendPN, IconUserPN } from '../../../icons'
import { useSelector } from 'react-redux'
import { AppState } from '@news-mono/web-common'
import { useDebugQueryParams } from '../../hooks/useDebugQueryParams'
import { EmailBanner } from '../../components/EmailBanner'
import { IconChevronRightTN } from '../../../icons/IconChevronRightTN/IconChevronRightTN'
import { tokens } from '@news-mono/design-tokens'

// PerthNow Colors
const pnColors = tokens.perthnow.colors
const pnPalette = pnColors.palette

interface SettingOptionProps {
    text: string
    onClick: () => void
    isActive?: boolean
    Icon: React.FC<any>
    screen: ScreenType
}

interface SettingsMenuItem {
    text: string
    screen: ScreenType
    Icon: React.FC<any>
}

export const PerthNowSettingsScreen = ({
    activeIndex,
    onMenuItemClick,
}: SettingsScreenInnerProps) => {
    const authState = useSelector((state: AppState) => state.authentication)
    const debugParams = useDebugQueryParams()

    const { userName, userEmail, emailVerified } = authState
    // Auth0 defaults userName as email
    const hasName = userName !== userEmail

    // Utilise debug params as a forefront state check
    const showBanner = debugParams.emailBanner || !emailVerified

    return (
        <>
            <h2>Settings</h2>
            {showBanner && (
                <StyledSettingVerifyEmailBanner>
                    <li>
                        <EmailBanner />
                    </li>
                </StyledSettingVerifyEmailBanner>
            )}
            <StyledSettingHeader>
                <li>
                    <p>{hasName ? userName : userEmail}</p>
                    {hasName ? <small>{userEmail}</small> : null}
                </li>
            </StyledSettingHeader>
            <StyledSettingBody>
                {getMenuItems().map((item, index) => (
                    <SettingOption
                        screen={item.screen}
                        text={item.text}
                        Icon={item.Icon}
                        isActive={index === activeIndex}
                        onClick={() => onMenuItemClick(index, item.screen)}
                    />
                ))}
            </StyledSettingBody>
        </>
    )
}

const getMenuItems = (): SettingsMenuItem[] => [
    {
        text: 'Manage account',
        screen: 'manage-account',
        Icon: IconUserPN,
    },
    {
        text: 'Newsletter',
        screen: 'email-preference',
        Icon: IconSendPN,
    },
    {
        text: 'About us',
        screen: 'about-us',
        Icon: IconPerthNowThumbnailPN,
    },
]

const SettingOption: React.FC<SettingOptionProps> = ({
    text,
    isActive,
    onClick,
    Icon,
    screen,
}) => {
    const redirectUrl =
        screen === 'email-preference'
            ? '/manage-email-preferences'
            : screen === 'about-us'
            ? '/about-us'
            : undefined

    return redirectUrl === undefined ? (
        <StyledFakeButton>
            <StyledPerthNowSettingOption isActive={isActive} onClick={onClick}>
                <div>
                    <Icon />
                    <p>{text}</p>
                    <IconChevronRightTN color={pnPalette.greyWeak} />
                </div>
            </StyledPerthNowSettingOption>
        </StyledFakeButton>
    ) : (
        <StyledPerthNowWebLink to={redirectUrl}>
            <StyledPerthNowSettingOption isActive={isActive}>
                <div>
                    <Icon />
                    <p>{text}</p>
                    <IconChevronRightTN color={pnPalette.greyWeak} />
                </div>
            </StyledPerthNowSettingOption>
        </StyledPerthNowWebLink>
    )
}
